import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('clippost_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('clippost_token');
      localStorage.removeItem('clippost_user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  uploadAvatar: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/auth/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const videoAPI = {
  upload: (file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/videos/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => onProgress?.(Math.round((e.loaded * 100) / e.total)),
      timeout: 600000,
    });
  },
  uploadBatch: (files, onProgress) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    return api.post('/videos/upload-batch', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => onProgress?.(Math.round((e.loaded * 100) / e.total)),
      timeout: 600000,
    });
  },
  list: () => api.get('/videos'),
  get: (id) => api.get(`/videos/${id}`),
  updateTranscript: (id, transcript) => api.put(`/videos/${id}/transcript`, { transcript }),
  downloadTranscript: (id, format, timecodes) => {
    const token = localStorage.getItem('clippost_token');
    const url = `${API}/videos/${id}/transcript/download?format=${format}&timecodes=${timecodes}`;
    return fetch(url, { headers: { Authorization: `Bearer ${token}` } }).then(res => {
      if (!res.ok) throw new Error('Download failed');
      return res.blob();
    });
  },
  exportSelected: (videoIds) => {
    const token = localStorage.getItem('clippost_token');
    return fetch(`${API}/videos/export-selected`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ video_ids: videoIds }),
    }).then(res => {
      if (!res.ok) throw new Error('Export failed');
      return res.blob();
    });
  },
  retry: (id) => api.post(`/videos/${id}/retry`),
};

export const presetAPI = {
  list: () => api.get('/presets'),
  get: (id) => api.get(`/presets/${id}`),
  create: (data) => api.post('/presets/create', data),
  update: (id, data) => api.put(`/presets/${id}`, data),
  publish: (id) => api.put(`/presets/${id}/publish`),
  unpublish: (id) => api.put(`/presets/${id}/unpublish`),
  delete: (id) => api.delete(`/presets/${id}`),
  chat: (message, chatHistory, draftPreset) => api.post('/presets/chat', {
    message, chat_history: chatHistory, draft_preset: draftPreset,
  }),
};

export const generateAPI = {
  create: (videoId, presetId) => api.post('/generate', { video_id: videoId, preset_id: presetId }),
  batchGenerate: (videoIds, presetId) => api.post('/batch/generate', { video_ids: videoIds, preset_id: presetId }),
  list: (videoId, presetId) => api.get(`/generations/${videoId}/${presetId}`),
  listByVideo: (videoId) => api.get(`/generations/by-video/${videoId}`),
  history: () => api.get('/generations/history'),
  historyEnriched: () => api.get('/generations/history-enriched'),
};

export const folderAPI = {
  list: () => api.get('/folders'),
  create: (name, color) => api.post('/folders', { name, color }),
  update: (id, data) => api.put(`/folders/${id}`, data),
  delete: (id) => api.delete(`/folders/${id}`),
  assignVideo: (videoId, folderId) => api.put(`/videos/${videoId}/folder`, { folder_id: folderId }),
  batchAssign: (videoIds, folderId) => api.put('/videos/batch-folder', { video_ids: videoIds, folder_id: folderId }),
};

export const batchAPI = {
  status: () => api.get('/batch/status'),
  all: () => api.get('/batch/all'),
  get: (id) => api.get(`/batch/${id}`),
};

export const dashboardAPI = {
  stats: () => api.get('/dashboard/stats'),
};

export const planAPI = {
  list: () => api.get('/plans'),
  select: (planId) => api.post('/plan/select', { plan_id: planId }),
  getCredits: () => api.get('/credits'),
  purchaseCredits: (packIndex) => api.post('/credits/purchase', { pack_index: packIndex }),
};

export const adminAPI = {
  me: () => api.get('/admin/me'),
  stats: () => api.get('/admin/stats'),
  listUsers: () => api.get('/admin/users'),
  updateUser: (id, data) => api.patch(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  bulkUpdate: (data) => api.post('/admin/users/bulk-update', data),
  bulkDelete: (data) => api.post('/admin/users/bulk-delete', data),
  auditLog: (params) => api.get('/admin/audit-log', { params }),
  listInvites: () => api.get('/admin/invites'),
  createInvite: (data) => api.post('/admin/invites', data),
  revokeInvite: (id) => api.delete(`/admin/invites/${id}`),
  listOrphans: (params) => api.get('/admin/orphaned-storage', { params }),
  purgeOrphans: () => api.post('/admin/orphaned-storage/purge'),
};

export const inviteAPI = {
  get: (token) => api.get(`/invites/${token}`),
  accept: (token, data) => api.post(`/invites/${token}/accept`, data),
};

export default api;
